import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@tyro/core/dialog';
import { useTranslation } from '@tyro/i18n';
import { TrashIcon } from '@tyro/icons';
import type { TimelineItemProps } from './item';
import { TimelineItemIcon } from './item-icon';
import { getEventDateTimes } from './utils';

interface SelectedEventsForHighlightsProps {
  open: boolean;
  events: TimelineItemProps['event'][];
  onClose: () => void;
  onRemoveEvent: (eventId: TimelineItemProps['event']['id']) => void;
}

export function SelectedEventsForHighlightsModal({
  open,
  events,
  onRemoveEvent,
  onClose,
}: SelectedEventsForHighlightsProps) {
  const { t } = useTranslation(['common', 'people']);

  const disableRemoveEvent = events.length === 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle onClose={onClose}>
        {t('people:eventSelectedForHighlight', { count: events.length })}
      </DialogTitle>
      <DialogContent>
        <List disablePadding>
          {events.map((event) => {
            const { monthTimestamp } = getEventDateTimes(event);
            return (
              <ListItem
                disableGutters
                key={JSON.stringify(event.id)}
                secondaryAction={
                  <Tooltip
                    title={
                      disableRemoveEvent
                        ? t('people:youMustHaveAtLeastOneEvent')
                        : t('common:actions.remove')
                    }
                  >
                    <span>
                      <IconButton
                        aria-label={t('common:actions.remove')}
                        disabled={disableRemoveEvent}
                        color="primary"
                        onClick={() => onRemoveEvent(event.id)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                }
              >
                <ListItemAvatar
                  sx={{
                    '& .timeline-item-icon': {
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      borderRadius: '50%',

                      '& svg': {
                        width: 22,
                        height: 22,
                      },
                    },
                  }}
                >
                  <TimelineItemIcon type={event.id.entityType!} />
                </ListItemAvatar>
                <ListItemText
                  primary={event.headline}
                  secondary={
                    <Box component="span">
                      <Typography variant="body2">{monthTimestamp}</Typography>
                      {event.details && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {event.details}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        <Button variant="soft" color="inherit" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
